import WorkSystemService from "../services/WorkSystemService";
import {applySnapshot, types} from 'mobx-state-tree';
import {rootStore} from '.';
import WorkSchduleService from 'services/WorkSchduleService';
import moment from 'moment-timezone';
import GroupService from 'services/GroupService';
import CompanyService from 'services/CompanyService';
import DepartmentService from 'services/DepartmentService';
import {ALL_OPTION} from 'pages/AuthMenu/Work/ShiftManagement/helpers';
import {getLocalizedField, localizedLabel} from 'helpers/localizedField';
import i18n from 'i18next';

moment.tz.setDefault('Asia/Seoul');

export const ShiftManagement = types
    .model('ShiftManagement', {
        // Filter states grouped by tab - each tab has its own filter parameters
        filterParams: types.optional(
            types.model('FilterParams', {
                tab: types.optional(types.string, 'templates'), // Current active tab

                // Templates tab filters
                templates: types.optional(
                    types.model('TemplatesFilters', {
                        name: types.optional(types.string, ''),
                        groupIds: types.optional(types.frozen(), null),
                        typeArray: types.optional(types.frozen(), null),
                        startTime: types.optional(types.string, ''),
                        endTime: types.optional(types.string, ''),
                    }),
                  {},
                ),

                // Personnel Assignment tab filters
                personnel: types.optional(
                    types.model('PersonnelFilters', {
                        userName: types.optional(types.string, ''),
                        groupId: types.optional(types.string, ''),
                        patternCycleId: types.optional(types.string, ''),
                    }),
                  {},
                ),

                // Schedule Pattern tab filters
                pattern: types.optional(
                    types.model('PatternFilters', {
                        patternCycleName: types.optional(types.string, ''),
                        parentGroupList: types.optional(types.frozen(), null),
                    }),
                  {},
                ),
            }),
          {},
        ),

        // Table data - tab specific
        templatesData: types.optional(types.array(types.frozen()), []),
        templatesDataCount: types.optional(types.number, 0),
        personnelData: types.optional(types.array(types.frozen()), []),
        personnelDataCount: types.optional(types.number, 0),
        patternData: types.optional(types.array(types.frozen()), []),
        patternDataCount: types.optional(types.number, 0),
        patternCycleList: types.optional(types.array(types.frozen()), []),

        // Common data
        page: types.optional(types.number, 1),
        size: types.optional(types.number, 500),
        isLoading: types.optional(types.boolean, false),

        workGroupLists: types.optional(types.array(types.frozen()), []),
        reduceTypeLists: types.optional(types.array(types.frozen()), []),
        restTypeLists: types.optional(types.array(types.frozen()), []),
        templateGroupsList: types.optional(types.array(types.frozen()), []),
        selectedSchedulePattern: types.optional(types.maybeNull(types.frozen()), null),
        selectedPersonal: types.optional(types.maybeNull(types.frozen()), null),
        employList: types.optional(types.array(types.frozen()), []),
    })
    .views((self) => ({
        get get() {
            try {
                return { ...self };
            } catch (err) {
                console.error(err);
                return err;
            }
        },

        // Convenience getters
        get tab() {
            return self.filterParams.tab;
        },

        // Get current tab's filter parameters
        get currentTabFilters() {
            // Check if gruopIds, typeArray, parentGroupList is ALL_OPTION or null then must be set to [] for API
            if (self.filterParams.tab === 'templates') {
                return {
                    ...self.filterParams.templates,
                    groupIds: [null, ALL_OPTION].includes(self.filterParams.templates.groupIds)
                        ? []
                        : self.filterParams.templates.groupIds,
                    typeArray: [null, ALL_OPTION].includes(self.filterParams.templates.typeArray)
                        ? []
                        : self.filterParams.templates.typeArray,
                };
            }
            if (self.filterParams.tab === 'pattern') {
                return {
                    ...self.filterParams.pattern,
                    parentGroupList: [null, ALL_OPTION].includes(self.filterParams.pattern.parentGroupList)
                        ? []
                        : self.filterParams.pattern.parentGroupList,
                };
            }

            if (self.filterParams.tab === 'personnel') {
                return {
                    ...self.filterParams.personnel,
                    patternCycleId: Number(self.filterParams.personnel.patternCycleId) || null,
                    groupId: [null, ALL_OPTION, ''].includes(self.filterParams.personnel.groupId)
                        ? null
                        : Number(self.filterParams.personnel.groupId),
                };
            }
            return self.filterParams[self.filterParams.tab];
        },

        // Tab-specific data getters
        get currentTabData() {
            switch (self.filterParams.tab) {
                case 'templates':
                    return self.templatesData;
                case 'personnel':
                    return self.personnelData;
                case 'pattern':
                    return self.patternData;
                default:
                    return self.templatesData;
            }
        },

        get currentTabDataCount() {
            switch (self.filterParams.tab) {
                case 'templates':
                    return self.templatesDataCount;
                case 'personnel':
                    return self.personnelDataCount;
                case 'pattern':
                    return self.patternDataCount;
                default:
                    return 0;
            }
        },
        get companyId() {
            return rootStore.company.get.id;
        },
    }))
    .actions((self) => ({
        reset() {
            console.log('RESET ShiftManagement MODEL');
            applySnapshot(self, {});
        },

        // Tab actions
        setTab(tab) {
            self.filterParams.tab = tab;
        },

        // Filter actions - Combined setter for current tab
        setFilterParams(params) {
            const currentFilters = self.filterParams[self.filterParams.tab];
            Object.keys(params).forEach((key) => {
                if (key in currentFilters) {
                    currentFilters[key] = params[key];
                }
            });
        },

        // Data setters for each tab
        setTemplatesData(data) {
            self.templatesData.replace(data);
        },

        setTemplatesDataCount(count) {
            self.templatesDataCount = count;
        },

        setPersonnelData(data) {
            self.personnelData.replace(data);
        },

        setPersonnelDataCount(count) {
            self.personnelDataCount = count;
        },

        setPatternData(data) {
            self.patternData.replace(data);
        },

        setPatternDataCount(count) {
            self.patternDataCount = count;
        },

        setWorkGroupLists(data) {
            self.workGroupLists.replace(data);
        },

        setReduceTypeLists(data) {
            self.reduceTypeLists.replace(data);
        },

        setRestTypeLists(data) {
            self.restTypeLists.replace(data);
        },

        // Set data for current tab
        setCurrentTabData(data, count) {
            switch (self.filterParams.tab) {
                case 'templates':
                    self.setTemplatesData(data);
                    self.setTemplatesDataCount(count);
                    break;
                case 'personnel':
                    self.setPersonnelData(data);
                    self.setPersonnelDataCount(count);
                    break;
                case 'pattern':
                    self.setPatternData(data);
                    self.setPatternDataCount(count);
                    break;
                default:
                    self.setTemplatesData(data);
                    self.setTemplatesDataCount(count);
            }
        },

        setPage(page) {
            self.page = page;
        },

        setIsLoading(loading) {
            self.isLoading = loading;
        },
        setTemplatesGroupList(data) {
            self.templateGroupsList = data.map((item) => ({
                value: item.id,
                label: item.name,
                code: item.code,
            }));
        },

        setSelectedSchedulePattern(data) {
            self.selectedSchedulePattern = data;
        },

        setSelectedPersonal(data) {
            self.selectedPersonal = data;
        },

        setEmployList(data) {
            self.employList.replace(data);
        },

        setPatternCycleList(data) {
            self.patternCycleList = data.map((item) => ({
                value: Number(item.patternCycleId),
                label: item.patternCycleName,
                code: item.patternCycleCode,
                parentGroupList: item.parentGroupList[0] || null,
            }));
        },

        // Reset filters for current tab
        resetFilters() {
            const resetMethods = {
                templates: () => this.resetTemplatesFilters(),
                personnel: () => this.resetPersonnelFilters(),
                pattern: () => this.resetPatternFilters(),
            };

            resetMethods[self.filterParams.tab]?.();
        },

        // Reset filters for templates tab
        resetTemplatesFilters() {
            const currentFilters = self.filterParams.templates;
            currentFilters.name = '';
            currentFilters.groupIds = null;
            currentFilters.typeArray = null;
            currentFilters.startTime = '';
            currentFilters.endTime = '';
        },

        // Reset filters for personnel tab
        resetPersonnelFilters() {
            const currentFilters = self.filterParams.personnel;
            currentFilters.userName = '';
            currentFilters.groupId = '';
            currentFilters.patternCycleId = '';
        },

        // Reset filters for pattern tab
        resetPatternFilters() {
            const currentFilters = self.filterParams.pattern;
            currentFilters.patternCycleName = '';
            currentFilters.parentGroupList = null;
        },

        // Reset data when changing tabs
        resetData() {
            self.page = 1;
        },

        // API calls - Mock data for now, replace with actual API calls
        async getShiftManagementList() {
            self.setIsLoading(true);

            const companyId = rootStore.company.get.id;
            const currentFilters = self.currentTabFilters;

            try {
                // TODO: Replace with actual API call
                // Determine which API to call based on tab
                const apiNameObj = {
                    templates: 'getTemplateList',
                    personnel: 'getPersonalList',
                    pattern: 'getSchedulePatternList',
                };

                const apiName = apiNameObj[self.filterParams.tab];
                if (!apiName) return;

                // First page load - independent of background pagination
                const res = await WorkSchduleService[apiName]({
                    companyId,
                    ...currentFilters,
                });

                if (res?.result !== true) {
                    return;
                }

                let result = res.data;
                if(self.filterParams.tab === 'templates') {
                    const templete = rootStore.user.me.permissions?.detailRole?.templete;
                    const typePermissionMap = {
                        WORK_SCHDULE: 'work',
                        REST_SCHDULE: 'vacation',
                        REDUCE_SCHDULE: 'reduce',
                    };

                    result = result.filter(item => {
                        const key = typePermissionMap[item.type];
                        return !!templete?.[key];
                    });
                }

                self.setCurrentTabData(result, result.length);

                return res;
            } catch (error) {
                console.error('Error loading shift management list:', error);
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },

        async getWorkGroupLists() {
            const res = await GroupService.searchSchdule({
                companyId: rootStore.company.get.id,
                isUser: true,
                workSystemName: '',
            });

            const data =
                res.data
                    .filter((v) => v.endAt === null || moment(v.endAt) > moment())
                    .map((v) => ({ ...v, value: v.id, label: getLocalizedField(v) })) || [];

            self.setWorkGroupLists(data || []);
        },
        async getReduceTypeLists() {
            await WorkSystemService.list(rootStore.company.get.id).then(res => {
                self.setReduceTypeLists(
                    res?.data?.filter(v => v.reduceWorkYn).map(v => {
                        return { id: v?.id, label: localizedLabel(i18n.t, v, 'subName') };
                    }) || []
                );
            });
        },
        async getRestTypes() {
            const res = await CompanyService.getUserRestType({
                companyId: rootStore.company.get.id,
                typeArray: ['REST', 'REST_CUSTOM'],
            });

            if (res?.data) {
                const types = res?.data || [];
                const typedata = [
                    { value: null, label: i18n.t('adminMode.popup.selectAnnualLeaveClassification') },
                    ...types
                        ?.filter((v) => v.option == 'REQUEST' || v.option == 'MANUAL')
                        // label은 표시용, 원본·언어별 이름은 엑셀 업로드에서 어느 언어로 적어도 맞추는 데 쓴다
                        .map((v) => ({ value: v.id, label: localizedLabel(i18n.t, v), name: v.name, nameEng: v.nameEng, nameVie: v.nameVie, nameChn: v.nameChn })),
                ];
                self.setRestTypeLists(typedata);
            }
        },

        async getEmployList() {
            try {
                const res = await DepartmentService.getDepartmentTreeUser(
                    rootStore.company.get.id,
                    'SUCCESS',
                );
                if (res?.data?.users) {
                    self.setEmployList(res.data.users);
                }
                return res?.data?.users || [];
            } catch (error) {
                console.error('Error fetching employee list:', error);
                return [];
            }
        },

        async getTemplateListByGroup(groupIds) {
            const companyId = rootStore.company.get.id;
            try {
                const res = await WorkSchduleService.getTemplateList({
                    companyId,
                    name: '',
                    groupIds: groupIds,
                    typeArray: [],
                    startTime: '',
                    endTime: '',
                });

                self.setTemplatesGroupList(res.data);

                return res;
            } catch (error) {
                console.error('Error loading template list by group:', error);
                return [];
            }
        },

        async createMultiTemplates(payload) {
            try {
                const res = await WorkSchduleService.createMultiTemplates(payload);

                return res;
            } catch (error) {}
        },

        async createSchedulePattern(payload) {
            const companyId = rootStore.company.get.id;

            try {
                const res = await WorkSchduleService.createSchedulePattern({
                    companyId,
                    ...payload,
                    cycleStartDt: moment.tz(payload.cycleStartDt, 'Asia/Seoul').format(),
                });
                if (res.result === true) {
                    self.getShiftManagementList();
                }
                return res;
            } catch (error) {
              console.error('Error creating schedule pattern:', error);
              return error;
            }
        },

        async createMultiPattern(payload, showNotification, t) {
            try {
                const res = await WorkSchduleService.createMultiSchedulePattern(payload);

                return res;
            } catch (error) {
              console.error('Error creating multi schedule pattern:', error);
                return error;
            }
        },

        async updateSchedulePattern(payload) {
            const companyId = rootStore.company.get.id;
            try {
                const res = await WorkSchduleService.updateSchedulePattern({
                    companyId,
                    ...payload,
                });

                if (res.result === true) {
                    self.getShiftManagementList();
                    self.setSelectedSchedulePattern(null);
                }
                return res;
            } catch (error) {
                console.error('Error updating schedule pattern:', error);
                return error;
            }
        },

        async deleteSchedulePattern(patternCycleId) {
            const companyId = rootStore.company.get.id;
            try {
                const res = await WorkSchduleService.deleteSchedulePattern({
                    companyId,
                    patternCycleId,
                });

                if (res.result === true) {
                    self.getShiftManagementList();
                    self.setSelectedSchedulePattern(null);
                }

                return res;
            } catch (error) {
                console.error('Error deleting schedule pattern:', error);
                return error;
            }
        },
        ////////////////////////
        async createPersonalAssignment(payload, isEdit = false) {
            const companyId = rootStore.company.get.id;
            const formatedPayload = payload.users.map((user) => ({
                companyId,
                userId: user.userId,
                patternCycleId: payload.patternCycleId,
                effectStartDt: payload.effectStartDt
                    ? moment.tz(payload.effectStartDt, 'Asia/Seoul').format()
                    : null,
                effectEndDt: moment.tz(payload.effectEndDt || '9999-12-31', 'Asia/Seoul').format(),
            }));

            try {
                const res = await WorkSchduleService[
                    isEdit ? 'updatePersonalAssignment' : 'createPersonalAssignment'
                ](formatedPayload);

                if (res.result === true) {
                    self.getShiftManagementList();
                    self.setSelectedPersonal(null);
                }

                return res;
            } catch (error) {
                console.error('Error creating personal assignment:', error);
                return error;
            }
        },

        async createMultiPersonalAssignment(payload) {
            try {
                const res = await WorkSchduleService.createPersonalAssignment(payload);
                if (res.result === true) {
                    self.getShiftManagementList();
                    self.setSelectedPersonal(null);
                }

                return res;
            } catch (error) {
                console.error('Error creating personal assignment:', error);
                return error;
            }
        },

        async deletePersonalAssignment(payload) {
            try {
                const res = await WorkSchduleService.deletePersonalAssignment({
                    ...payload,
                    companyId: payload.users[0]?.companyId,
                    userId: payload.users[0]?.userId,
                });
                if (res.result === true) {
                    self.getShiftManagementList();
                    self.setSelectedPersonal(null);
                }
                return res;
            } catch (error) {
                console.error('Error deleting personal assignment:', error);
                return error;
            }
        },
        async getPatternCycleList() {
            const companyId = rootStore.company.get.id;
            try {
                const res = await WorkSchduleService.getSchedulePatternList({
                    companyId,
                    patternCycleName: '',
                    parentGroupList: [],
                });

                self.setPatternCycleList(res.data);

                return res;
            } catch (error) {
                console.error('Error loading pattern cycle list:', error);
                return [];
            }
        },

        // Initialize all data
        async initialize() {
            if (self.isLoading) {
                return;
            }

            try {
                await Promise.all([
                    this.getShiftManagementList(),
                    this.getWorkGroupLists(),
                    this.getReduceTypeLists(),
                    this.getRestTypes(),
                ]);
            } catch (error) {
                console.error('Error initializing ShiftManagement:', error);
            }
        },
    }));

export default ShiftManagement;
